import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../../services/api';

const AdminQrMapping = () => {
    const { t } = useTranslation();
    const [allQrCodes, setAllQrCodes] = useState([]);
    const [allProfiles, setAllProfiles] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [successAnimation, setSuccessAnimation] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    
    // New state for menu and modals
    const [openMenuId, setOpenMenuId] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, qrCode: null, message: '' });
    const [assignUserModalOpen, setAssignUserModalOpen] = useState(false);
    const [assignProfileModalOpen, setAssignProfileModalOpen] = useState(false);
    const [selectedQrForAssign, setSelectedQrForAssign] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedProfileId, setSelectedProfileId] = useState('');
    const [processing, setProcessing] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [profileSearchTerm, setProfileSearchTerm] = useState('');
    const menuRef = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem('jwtToken');
        const role = localStorage.getItem('userRole');
        if (!token || role !== 'ADMIN') {
            window.location.replace('/');
            return;
        }
        loadData();
    }, []);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [unassignedResponse, assignedResponse, profilesResponse, usersResponse] = await Promise.all([
                fetchWithAuth('/qrcodes/unassigned'),
                fetchWithAuth('/qrcodes/assigned'),
                fetchWithAuth('/profiles'),
                fetchWithAuth('/admin/users')
            ]);

            const allQrCodes = [...(unassignedResponse || []), ...(assignedResponse || [])];
            
            // Sort by status priority
            const statusPriority = {
                'TAKEN': 1,
                'AVAILABLE_ENGRAVED': 2,
                'IN_PRODUCTION': 3,
                'READY_FOR_PRODUCTION': 4,
                'AVAILABLE': 5
            };
            
            allQrCodes.sort((a, b) => {
                const statusA = a.status || (a.assigned ? 'TAKEN' : 'AVAILABLE');
                const statusB = b.status || (b.assigned ? 'TAKEN' : 'AVAILABLE');
                
                const priorityA = statusPriority[statusA] || 6;
                const priorityB = statusPriority[statusB] || 6;
                
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }
                
                const numA = parseInt(a.code.replace(/\D/g, '') || '0');
                const numB = parseInt(b.code.replace(/\D/g, '') || '0');
                return numA - numB;
            });

            // Assign permanent sequence IDs based on initial sort order
            allQrCodes.forEach((qr, index) => {
                qr.sequenceId = index + 1;
            });

            setAllQrCodes(allQrCodes);
            setAllProfiles(profilesResponse || []);
            setAllUsers(usersResponse || []);
            
        } catch (error) {
            console.error('Error loading data:', error);
            setMessage({ 
                type: 'error', 
                text: t('qrMapping.loadError', `Failed to load data: ${error.message}`)
            });
        } finally {
            setLoading(false);
        }
    };

    const toggleMenu = (qrCode) => {
        setOpenMenuId(openMenuId === qrCode ? null : qrCode);
    };

    const getQrCodeSize = (qrCode) => {
        if (!qrCode) return null;
        const code = qrCode.toLowerCase();
        if (code.endsWith('s')) return 'S';
        if (code.endsWith('m')) return 'M';
        if (code.endsWith('b')) return 'L';
        return null;
    };

    // Assign/Reassign Modal handlers
    // Open Assign to User Modal
    const openAssignUserModal = (qrCode) => {
        setSelectedQrForAssign({ code: qrCode.code, data: qrCode });
        setSelectedUserId('');
        setUserSearchTerm('');
        setAssignUserModalOpen(true);
        setOpenMenuId(null);
    };

    const closeAssignUserModal = () => {
        setAssignUserModalOpen(false);
        setSelectedQrForAssign(null);
        setSelectedUserId('');
        setUserSearchTerm('');
    };

    // Open Assign to Profile Modal
    const openAssignProfileModal = (qrCode, isReassign = false) => {
        setSelectedQrForAssign({ code: qrCode.code, isReassign, data: qrCode });
        setSelectedProfileId('');
        setProfileSearchTerm('');
        setAssignProfileModalOpen(true);
        setOpenMenuId(null);
    };

    const closeAssignProfileModal = () => {
        setAssignProfileModalOpen(false);
        setSelectedQrForAssign(null);
        setSelectedProfileId('');
        setProfileSearchTerm('');
    };

    // Handle Assign to User
    const handleAssignToUser = async () => {
        if (!selectedUserId) {
            setMessage({ type: 'error', text: t('qrMapping.selectUser', 'Please select a user.') });
            return;
        }

        setProcessing(true);
        setMessage({ type: '', text: '' });

        try {
            // Need to create backend endpoint for this
            await fetchWithAuth('/qrcodes/admin/assign-to-user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    qrCode: selectedQrForAssign.code,
                    userId: parseInt(selectedUserId)
                })
            });

            const user = allUsers.find(u => u.id === parseInt(selectedUserId));
            setMessage({ 
                type: 'success', 
                text: t('qrMapping.assignUserSuccess', `Successfully assigned ${selectedQrForAssign.code} to ${user?.fullName || user?.email}.`)
            });
            
            closeAssignUserModal();
            await loadData();
            
        } catch (error) {
            console.error('Error assigning QR code to user:', error);
            setMessage({ 
                type: 'error', 
                text: t('qrMapping.assignError', error.message || 'Failed to assign QR code to user.')
            });
        } finally {
            setProcessing(false);
        }
    };

    // Handle Assign to Profile
    const handleAssignToProfile = async () => {
        if (!selectedProfileId) {
            setMessage({ type: 'error', text: t('qrMapping.selectProfile', 'Please select a profile.') });
            return;
        }

        const profileId = parseInt(selectedProfileId);
        const profile = allProfiles.find(p => p.id === profileId);
        if (!profile) {
            setMessage({ type: 'error', text: t('qrMapping.profileNotFound', `Profile not found.`) });
            return;
        }

        setProcessing(true);
        setMessage({ type: '', text: '' });

        try {
            // Automatically use reassign if QR code is already assigned to ANY user or profile
            const qrData = selectedQrForAssign.data;
            const isAlreadyAssigned = qrData.userEmail || qrData.profileId;
            const endpoint = isAlreadyAssigned ? '/qrcodes/reassign' : '/qrcodes/assign';
            
            await fetchWithAuth(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    qrCode: selectedQrForAssign.code,
                    profileId: profileId
                })
            });

            const action = isAlreadyAssigned ? 'reassigned' : 'assigned';
            setMessage({ 
                type: 'success', 
                text: t(`qrMapping.${action}Success`, `Successfully ${action} ${selectedQrForAssign.code} to ${profile.name}.`)
            });
            
            closeAssignProfileModal();
            await loadData();
            
        } catch (error) {
            console.error('Error assigning QR code to profile:', error);
            setMessage({ 
                type: 'error', 
                text: t('qrMapping.assignError', error.message || 'Failed to assign QR code to profile.')
            });
        } finally {
            setProcessing(false);
        }
    };

    // Filter users based on search term (checks both name and email with contains logic)
    const getFilteredUsers = () => {
        if (!userSearchTerm) return allUsers;
        
        const searchLower = userSearchTerm.toLowerCase();
        return allUsers.filter(user => {
            const fullName = (user.fullName || '').toLowerCase();
            const email = (user.email || '').toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    };

    // Filter profiles based on search term (checks profile name and owner)
    const getFilteredProfiles = () => {
        if (!profileSearchTerm) return allProfiles;
        
        const searchLower = profileSearchTerm.toLowerCase();
        return allProfiles.filter(profile => {
            const profileName = (profile.name || '').toLowerCase();
            const ownerName = (profile.userFullName || '').toLowerCase();
            const ownerEmail = (profile.userEmail || '').toLowerCase();
            return profileName.includes(searchLower) || ownerName.includes(searchLower) || ownerEmail.includes(searchLower);
        });
    };

    // Confirmation dialog handlers
    const showConfirmDialog = (action, qrCode, message) => {
        setConfirmDialog({ isOpen: true, action, qrCode, message });
        setOpenMenuId(null);
    };

    const cancelConfirmDialog = () => {
        setConfirmDialog({ isOpen: false, action: null, qrCode: null, message: '' });
    };

    const handleConfirmAction = async () => {
        const { action, qrCode } = confirmDialog;
        setProcessing(true);
        setMessage({ type: '', text: '' });
        cancelConfirmDialog();

        try {
            await fetchWithAuth(`/qrcodes/admin/${action}/${qrCode}`, {
                method: 'POST'
            });

            let successMessage = '';
            if (action === 'unassign-from-user') {
                successMessage = t('qrMapping.unassignUserSuccess', `Successfully unassigned ${qrCode} from user and profile.`);
            } else if (action === 'unassign-from-profile') {
                successMessage = t('qrMapping.unassignProfileSuccess', `Successfully unassigned ${qrCode} from profile.`);
            } else if (action === 'total-reset') {
                successMessage = t('qrMapping.totalResetSuccess', `Successfully performed total reset on ${qrCode}.`);
            }

            setMessage({ type: 'success', text: successMessage });
            await loadData();

        } catch (error) {
            console.error(`Error performing ${action}:`, error);
            setMessage({ 
                type: 'error', 
                text: t('qrMapping.actionError', error.message || `Failed to perform ${action}.`)
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleUnassignFromUser = (qrCode) => {
        showConfirmDialog(
            'unassign-from-user',
            qrCode.code,
            t('qrMapping.confirmUnassignUser', `Are you sure you want to unassign ${qrCode.code} from the user? This will also unassign it from the profile.`)
        );
    };

    const handleUnassignFromProfile = (qrCode) => {
        showConfirmDialog(
            'unassign-from-profile',
            qrCode.code,
            t('qrMapping.confirmUnassignProfile', `Are you sure you want to unassign ${qrCode.code} from the profile? The user assignment will be kept.`)
        );
    };

    const handleTotalReset = (qrCode) => {
        showConfirmDialog(
            'total-reset',
            qrCode.code,
            t('qrMapping.confirmTotalReset', `Are you sure you want to perform a TOTAL RESET on ${qrCode.code}? This will remove both user and profile assignments and reset the status to AVAILABLE. This action cannot be undone.`)
        );
    };

    const handleStatusUpdate = async (qrCode, newStatus) => {
        setUpdatingStatus(prev => ({ ...prev, [qrCode]: true }));
        setMessage({ type: '', text: '' });

        try {
            await fetchWithAuth('/qrcodes/update-status', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    qrCode: qrCode,
                    status: newStatus
                })
            });

            setMessage({ 
                type: 'success', 
                text: t('qrMapping.statusUpdateSuccess', `Successfully updated ${qrCode} status to ${newStatus}.`)
            });
            
            setSuccessAnimation(prev => ({ ...prev, [qrCode]: true }));
            setTimeout(() => {
                setSuccessAnimation(prev => ({ ...prev, [qrCode]: false }));
            }, 1000);

            await loadData();
            
        } catch (error) {
            console.error('Error updating status:', error);
            setMessage({ 
                type: 'error', 
                text: t('qrMapping.statusUpdateError', error.message || 'Failed to update QR code status.')
            });
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [qrCode]: false }));
        }
    };

    // Sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedQrCodes = () => {
        if (!sortConfig.key) return allQrCodes;

        return [...allQrCodes].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            // Sort by sequenceId (permanent ID assigned on load)
            if (sortConfig.key === 'id') {
                aValue = a.sequenceId || 0;
                bValue = b.sequenceId || 0;
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (sortConfig.key === 'code') {
                const numA = parseInt(a.code.replace(/\D/g, '') || '0');
                const numB = parseInt(b.code.replace(/\D/g, '') || '0');
                return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
            }

            if (sortConfig.key === 'userEmail') {
                aValue = a.userEmail || '';
                bValue = b.userEmail || '';
            }

            if (sortConfig.key === 'profileName') {
                aValue = a.profileName || '';
                bValue = b.profileName || '';
            }

            // Sort by profile views (scans)
            if (sortConfig.key === 'profileViews') {
                aValue = a.profileViews || 0;
                bValue = b.profileViews || 0;
                return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            }

            if (sortConfig.key === 'status') {
                const statusPriority = {
                    'TAKEN': 1,
                    'AVAILABLE_ENGRAVED': 2,
                    'IN_PRODUCTION': 3,
                    'READY_FOR_PRODUCTION': 4,
                    'AVAILABLE': 5
                };
                const priorityA = statusPriority[aValue] || 6;
                const priorityB = statusPriority[bValue] || 6;
                return sortConfig.direction === 'asc' ? priorityA - priorityB : priorityB - priorityA;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) {
            return <span className="ml-1 text-gray-400">⇅</span>;
        }
        return (
            <span className="ml-1">
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'AVAILABLE':
                return 'bg-green-100 text-green-800';
            case 'TAKEN':
                return 'bg-blue-100 text-blue-800';
            case 'IN_PRODUCTION':
                return 'bg-yellow-100 text-yellow-800';
            case 'READY_FOR_PRODUCTION':
                return 'bg-purple-100 text-purple-800';
            case 'AVAILABLE_ENGRAVED':
                return 'bg-teal-100 text-teal-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-xl text-gray-600">{t('common.loading', 'Loading...')}</div>
            </div>
        );
    }

    const sortedQrCodes = getSortedQrCodes();

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">
                        {t('qrMapping.title', 'QR Code Management')}
                    </h1>

                    {message.text && (
                        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                            {message.text}
                        </div>
                    )}

                    <div className="mb-4 text-sm text-gray-600">
                        <p>{t('qrMapping.totalQrCodes', 'Total QR Codes')}: {allQrCodes.length}</p>
                        <p>{t('qrMapping.assignedQrCodes', 'Assigned')}: {allQrCodes.filter(q => q.userEmail).length}</p>
                        <p>{t('qrMapping.unassignedQrCodes', 'Unassigned')}: {allQrCodes.filter(q => !q.userEmail).length}</p>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('qrMapping.actions', 'Actions')}
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('id')}
                                    >
                                        {t('qrMapping.qrId', 'QR ID')}
                                        <SortIcon columnKey="id" />
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('code')}
                                    >
                                        {t('qrMapping.qrCode', 'QR Code')}
                                        <SortIcon columnKey="code" />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        {t('qrMapping.size', 'Size')}
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('status')}
                                    >
                                        {t('qrMapping.status', 'Status')}
                                        <SortIcon columnKey="status" />
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('userEmail')}
                                    >
                                        {t('qrMapping.assignedUser', 'Assigned User')}
                                        <SortIcon columnKey="userEmail" />
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('profileName')}
                                    >
                                        {t('qrMapping.assignedProfile', 'Assigned Profile')}
                                        <SortIcon columnKey="profileName" />
                                    </th>
                                    <th 
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                        onClick={() => handleSort('profileViews')}
                                    >
                                        {t('qrMapping.totalScans', 'Total Scans')}
                                        <SortIcon columnKey="profileViews" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {sortedQrCodes.map((qr) => (
                                    <tr key={qr.code} className={successAnimation[qr.code] ? 'bg-green-50 transition-colors duration-500' : ''}>
                                        {/* Actions Menu */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="relative" ref={openMenuId === qr.code ? menuRef : null}>
                                                <button
                                                    onClick={() => toggleMenu(qr.code)}
                                                    className="text-gray-600 hover:text-gray-900 focus:outline-none"
                                                >
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                    </svg>
                                                </button>
                                                
                                                {openMenuId === qr.code && (
                                                    <div className="absolute left-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                                                        <div className="py-1">
                                                            <button
                                                                onClick={() => openAssignUserModal(qr)}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                            >
                                                                {t('qrMapping.assignToUser', 'Assign to User')}
                                                            </button>
                                                            <button
                                                                onClick={() => openAssignProfileModal(qr, false)}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                            >
                                                                {t('qrMapping.assignToProfile', 'Assign to Profile')}
                                                            </button>
                                                            {qr.userEmail && (
                                                                <button
                                                                    onClick={() => handleUnassignFromUser(qr)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                                                                >
                                                                    {t('qrMapping.unassignFromUser', 'Unassign from User (and Profile)')}
                                                                </button>
                                                            )}
                                                            {qr.profileId && (
                                                                <button
                                                                    onClick={() => handleUnassignFromProfile(qr)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                                                                >
                                                                    {t('qrMapping.unassignFromProfile', 'Unassign from Profile')}
                                                                </button>
                                                            )}
                                                            {(qr.userEmail || qr.profileId) && (
                                                                <button
                                                                    onClick={() => handleTotalReset(qr)}
                                                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t border-gray-200"
                                                                >
                                                                    {t('qrMapping.totalReset', 'Total Reset')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* QR ID (permanent sequence number) */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            #{qr.sequenceId}
                                        </td>

                                        {/* QR Code */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {qr.code}
                                        </td>

                                        {/* Size */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getQrCodeSize(qr.code) && (
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    getQrCodeSize(qr.code) === 'S' ? 'bg-blue-100 text-blue-800' :
                                                    getQrCodeSize(qr.code) === 'M' ? 'bg-green-100 text-green-800' :
                                                    'bg-purple-100 text-purple-800'
                                                }`}>
                                                    {getQrCodeSize(qr.code)}
                                                </span>
                                            )}
                                        </td>

                                        {/* Status (merged with update functionality) */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <select
                                                value={qr.status}
                                                onChange={(e) => handleStatusUpdate(qr.code, e.target.value)}
                                                disabled={updatingStatus[qr.code]}
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${getStatusBadgeColor(qr.status)}`}
                                            >
                                                <option value="AVAILABLE" className="bg-white text-gray-900">AVAILABLE</option>
                                                <option value="TAKEN" className="bg-white text-gray-900">TAKEN</option>
                                                <option value="IN_PRODUCTION" className="bg-white text-gray-900">IN_PRODUCTION</option>
                                                <option value="READY_FOR_PRODUCTION" className="bg-white text-gray-900">READY_FOR_PRODUCTION</option>
                                                <option value="AVAILABLE_ENGRAVED" className="bg-white text-gray-900">AVAILABLE_ENGRAVED</option>
                                            </select>
                                        </td>

                                        {/* Assigned User */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {qr.userFullName || qr.userEmail ? (
                                                <div className="flex flex-col">
                                                    {qr.userFullName && (
                                                        <span className="font-medium text-gray-900">{qr.userFullName}</span>
                                                    )}
                                                    {qr.userEmail && (
                                                        <span className="text-gray-500 text-xs">{qr.userEmail}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400">{t('qrMapping.notAssigned', 'Not assigned')}</span>
                                            )}
                                        </td>

                                        {/* Assigned Profile */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {qr.profileName ? (
                                                <span>
                                                    {qr.profileName} 
                                                    <span className="text-gray-500 ml-1">(ID: {qr.profileId})</span>
                                                </span>
                                            ) : (
                                                <span className="text-gray-400">{t('qrMapping.notAssigned', 'Not assigned')}</span>
                                            )}
                                        </td>

                                        {/* Total Scans (now sortable) */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <div className="flex items-center">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    (qr.profileViews || 0) > 0 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {qr.profileViews || 0} {t('qrMapping.scans', 'scans')}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Assign to User Modal */}
            {assignUserModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white max-h-[80vh] flex flex-col">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('qrMapping.assignToUser', 'Assign QR Code to User')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            QR Code: <span className="font-semibold">{selectedQrForAssign?.code}</span>
                        </p>
                        
                        {/* Search Input */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('qrMapping.searchUser', 'Search User')}
                            </label>
                            <input
                                type="text"
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t('qrMapping.searchUserPlaceholder', 'Search by name or email...')}
                                autoFocus
                            />
                        </div>

                        {/* Users List */}
                        <div className="mb-4 flex-1 overflow-y-auto max-h-[300px] border border-gray-300 rounded-md">
                            {getFilteredUsers().length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    {t('qrMapping.noUsersFound', 'No users found')}
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {getFilteredUsers().map((user) => (
                                        <label
                                            key={user.id}
                                            className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                                selectedUserId === user.id.toString() ? 'bg-blue-50' : ''
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="user-selection"
                                                value={user.id}
                                                checked={selectedUserId === user.id.toString()}
                                                onChange={(e) => setSelectedUserId(e.target.value)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <div className="ml-3 flex-1">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.fullName || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {user.email}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={closeAssignUserModal}
                                disabled={processing}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleAssignToUser}
                                disabled={processing || !selectedUserId}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {processing ? t('common.processing', 'Processing...') : t('common.confirm', 'Assign')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign to Profile Modal */}
            {assignProfileModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white max-h-[80vh] flex flex-col">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {selectedQrForAssign?.isReassign 
                                ? t('qrMapping.reassignToProfile', 'Reassign QR Code to Profile')
                                : t('qrMapping.assignToProfile', 'Assign QR Code to Profile')
                            }
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            QR Code: <span className="font-semibold">{selectedQrForAssign?.code}</span>
                        </p>
                        
                        {/* Search Input */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('qrMapping.searchProfile', 'Search Profile')}
                            </label>
                            <input
                                type="text"
                                value={profileSearchTerm}
                                onChange={(e) => setProfileSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={t('qrMapping.searchProfilePlaceholder', 'Search by profile name or owner...')}
                                autoFocus
                            />
                        </div>

                        {/* Profiles List */}
                        <div className="mb-4 flex-1 overflow-y-auto max-h-[300px] border border-gray-300 rounded-md">
                            {getFilteredProfiles().length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    {t('qrMapping.noProfilesFound', 'No profiles found')}
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200">
                                    {getFilteredProfiles().map((profile) => (
                                        <label
                                            key={profile.id}
                                            className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                                                selectedProfileId === profile.id.toString() ? 'bg-blue-50' : ''
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="profile-selection"
                                                value={profile.id}
                                                checked={selectedProfileId === profile.id.toString()}
                                                onChange={(e) => setSelectedProfileId(e.target.value)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                            />
                                            <div className="ml-3 flex-1">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {profile.name}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    Owner: {profile.userFullName || profile.userEmail || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    ID: {profile.id}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={closeAssignProfileModal}
                                disabled={processing}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleAssignToProfile}
                                disabled={processing || !selectedProfileId}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {processing ? t('common.processing', 'Processing...') : t('common.confirm', 'Assign')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {confirmDialog.isOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('common.confirmAction', 'Confirm Action')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-6">
                            {confirmDialog.message}
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={cancelConfirmDialog}
                                disabled={processing}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                            >
                                {t('common.cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={handleConfirmAction}
                                disabled={processing}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                                    confirmDialog.action === 'total-reset' 
                                        ? 'bg-red-600 hover:bg-red-700' 
                                        : 'bg-orange-600 hover:bg-orange-700'
                                }`}
                            >
                                {processing ? t('common.processing', 'Processing...') : t('common.confirm', 'Confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminQrMapping;
